/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/Form';
import { getCustomChildItemTypes } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Form';
import { labelAlignment } from '../../registry/constants';

const FormManager: ComponentManagerFn<'Form' | 'SearchForm'> = (
  platform: Platform,
  type: 'Form' | 'SearchForm',
): ComponentManager<'Form' | 'SearchForm'> => {
  return {
    ...baseComponentManager<'Form' | 'SearchForm'>(platform, type),
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('Form'),
          'AsyncSelect',
          'AttachmentField',
          'Box',
          'Checkbox',
          'ControllerComponent',
          'DateField',
          'IconSelect',
          'LOVField',
          'LookupSelect',
          'Media',
          'MultiField',
          'NumberField',
          'RadioBox',
          'RichTextEditor',
          'Select',
          'TextField',
          'MutliSelectLOVField',
          'ExpressionBuilderField',
          'MultiDateSelector',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      formHorizontal: {
        label: 'Form Horizontal',
        type: 'boolean',
        helpText:
          'If checked, the form field lables will be displayed left to the input.',
      },
      labelWidth: {
        label: 'Label Width',
        type: 'number',
        min: 120,
        max: 400,
        step: 20,
        helpText:
          'Defines the number of pixels that the form field labels are going to use.',
      },
      labelAlignment: {
        label: 'Label Alignment',
        type: 'select',
        options: labelAlignment,
      },
      spacing: {
        label: 'Space Between Fields',
        type: 'number',
        min: 0,
        max: 4,
        step: 1,
      },
      formMinWidth: {
        label: 'Form Min Width',
        type: 'string',
        placeholder: 'e.g. 1 or 100% or 500px',
      },
      formMaxWidth: {
        label: 'Form Max Width',
        type: 'string',
        placeholder: 'e.g. 1 or 100% or 500px',
      },
      plain: {
        label: 'Plain Form',
        type: 'boolean',
        helpText:
          'If checked, readonly fields will be rendered as plain text instead of a disabled input look.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'labelWidth':
        case 'labelAlignment':
          return itemData?.formHorizontal;
        default:
          return true;
      }
    },
    name: 'Form',
    canHaveChildren: () => true,
    category: 'Forms',
    getDisplayText: (item) =>
      item.props.formHorizontal ? 'Horizontal' : 'Vertical',
    icon: { iconName: 'file-invoice' },
    description: `**Overview**<br/>
The Form component is the fundamental container for data entry interfaces. It structures and aligns input fields—such as text boxes, date pickers, and dropdowns—into a cohesive layout. It is designed to group related data collection controls, managing their spacing, alignment, and label positioning uniformly.
Use this component as the wrapper for any screen requiring user input, such as registration pages, settings panels, or search filters.

**1. Layout & Orientation**<br/>
The Form component offers two primary layout modes to adapt to different screen sizes and design requirements.
* **Vertical (Default):** Labels are displayed **above** the input fields. (Usage: Best for mobile responsiveness or when labels are long).
* **Horizontal (formHorizontal):** Labels are displayed to the **left** of the input fields. (Usage: Best for desktop applications where vertical space is at a premium).
* **Label Width:** (Visible only when Horizontal) Defines the fixed width (in pixels) allocated to labels. Range: 120px - 400px.
* **Label Alignment:** Controls whether the text inside the label column is aligned to the left, right, or center.

**2. Spacing & Dimensions**<br/>
You can fine-tune the density and constraints of the form to fit your page design.
* **Space Between Fields:** Controls the vertical margin between distinct input rows. (Range: 0 Compact to 4 Spacious).
* **Form Min/Max Width:** Accepts CSS values (e.g., "100%", "500px") to constrain the form's width, preventing it from stretching too wide on large monitors.

**3. Visual Styling**<br/>
* **Plain Form:** A boolean toggle that changes the rendering of read-only fields.
    * **Disabled (Default):** Read-only fields look like grayed-out input boxes.
    * **Plain (Checked):** Read-only fields render as standard text without the input box border. This is ideal for "Review" screens where data should look like a document.

**4. Supported Children**<br/>
The Form component is a container designed specifically to host input-type components.
* **Basic Inputs:** TextField, NumberField, Checkbox, RadioBox.
* **Advanced Inputs:** DateField, Select, AsyncSelect, RichTextEditor, ExpressionBuilderField.
* **Layout & Media:** Box (for grouping), AttachmentField, Media.

**5. Best Use Cases**<br/>
* **User Registration:** A vertical form collecting Name, Email, and Password.
* **Back-Office Data Entry:** A dense horizontal form for rapid financial record input.
* **Search Panels:** A horizontal SearchForm variant allowing users to filter grid results.
* **Details View:** A "Plain" form displaying a read-only summary of a customer's profile.`,
    helpRenders: [
      {
        title: 'Default Form',
        page: SamplePage,
        exampleId: 'e1',
        height: 300,
        visibleProps: [],
        hideProps: true,
        item: {
          children: [
            'textField',
            'dateField',
            'numberField',
            'checkbox',
            'dateField1',
            'select',
            'checkbox1',
            'radioBox',
          ],
          itemId: 'form',
          parent: 'dataStoreReference',
          props: {},
          type: 'Form',
        },
      },
      {
        title: 'Horizontal Form',
        description: 'Try changing the label Width & Alignment.',
        page: SamplePage,
        exampleId: 'e2',
        height: 300,
        visibleProps: ['formHorizontal', 'labelAlignment', 'labelWidth'],
        item: {
          children: [
            'textField',
            'dateField',
            'numberField',
            'checkbox',
            'dateField1',
            'select',
            'checkbox1',
            'radioBox',
          ],
          itemId: 'form',
          parent: 'dataStoreReference',
          props: {
            formHorizontal: true,
          },
          type: 'Form',
        },
      },
    ],
  };
};

export default FormManager;
